// File: src/Subprogram/AdminPanel.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  User, Lock, LogOut, FileText, MessageSquare, 
  CheckCircle, Trash2, Plus, Loader2, AlertCircle, 
  Box, LayoutGrid, DollarSign, Image as ImageIcon, Link as LinkIcon, Layers, Edit, X,
  Eye, EyeOff, Settings, FolderTree, Tag 
} from 'lucide-react';

const API_BASE = 'https://free3dmax.ir/api';

const AdminPanel = ({ onBack }) => {
    // --- State ورود ---
    const [isAdmin, setIsAdmin] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false); 
    const [loginError, setLoginError] = useState('');
    
    // --- State تب اصلی ---
    const [mainTab, setMainTab] = useState('products'); // products | content | categories

    // --- State داده‌ها ---
    const [news, setNews] = useState([]);
    const [comments, setComments] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);

    // --- State فرم‌ها ---
    const [activeCommentTab, setActiveCommentTab] = useState('pending');
    
    const [newNewsTitle, setNewNewsTitle] = useState('');
    const [newNewsText, setNewNewsText] = useState('');
    const [isSubmittingNews, setIsSubmittingNews] = useState(false);

    // استیت دسته‌بندی‌ها
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [categoryForm, setCategoryForm] = useState({ slug: '', label: '', icon: 'Box' });
    const [isSubmittingCategory, setIsSubmittingCategory] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [productForm, setProductForm] = useState({
        title: '', 
        category: '', 
        renderer: 'V-Ray', 
        image: '', 
        file_size: '', 
        price: 0,
        description: '',
        polys: '',
        verts: '',
        formats: '',
        textures: '',
        uv_mapping: 'Yes',
        download_link: ''
    });
    const [isSubmittingProduct, setIsSubmittingProduct] = useState(false);

    // --- لاگین ---
    const handleLogin = (e) => {
        e.preventDefault();
        if (username === 'admin1368' && password === 'JustDoIt1368') {
            setIsAdmin(true);
            setLoginError('');
            fetchData();
        } else {
            setLoginError('نام کاربری یا رمز عبور اشتباه است.');
        }
    };

    // --- دریافت داده‌ها ---
    const fetchData = async () => {
        setLoading(true);
        try {
            const [newsRes, commentsRes, productsRes, categoriesRes] = await Promise.all([
                axios.get(`${API_BASE}/news.php`),
                axios.get(`${API_BASE}/comments.php?approved=0`),
                axios.get(`${API_BASE}/products.php`),
                axios.get(`${API_BASE}/categories.php`) // دریافت دسته‌بندی‌ها
            ]);

            setNews(Array.isArray(newsRes.data) ? newsRes.data : []);
            setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
            
            const cats = Array.isArray(categoriesRes.data) ? categoriesRes.data : [];
            setCategories(cats);
            
            // تنظیم دسته بندی پیش فرض فرم محصولات
            if(cats.length > 0) {
                setProductForm(prev => ({ ...prev, category: cats[0].slug }));
            }

            const approvedRes = await axios.get(`${API_BASE}/comments.php?approved=1`);
            const pendingList = (Array.isArray(commentsRes.data) ? commentsRes.data : []).map(c => ({
                ...c, is_approved: c.approved === 1 || c.approved === '1' || c.is_approved === true || false
            }));
            const approvedList = (Array.isArray(approvedRes.data) ? approvedRes.data : []).map(c => ({
                ...c, is_approved: true 
            }));

            const allComments = [...pendingList, ...approvedList];
            const uniqueComments = Array.from(new Map(allComments.map(item => [item.id, item])).values());
            uniqueComments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setComments(uniqueComments);

        } catch (error) {
            console.error("Error fetching data", error);
        } finally {
            setLoading(false);
        }
    };

    // --- مدیریت محصولات ---
    const handleInputChange = (e) => {
        setProductForm({...productForm, [e.target.name]: e.target.value});
    };

    const startEditing = (prod) => {
        setEditingId(prod.id);
        setProductForm({
            title: prod.title, category: prod.category, renderer: prod.renderer, image: prod.image,
            file_size: prod.file_size, price: prod.price, description: prod.description || '',
            polys: prod.polys || '', verts: prod.verts || '', formats: prod.formats || '',
            textures: prod.textures || '', uv_mapping: prod.uv_mapping || 'Yes', download_link: prod.download_link || ''
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelEditing = () => {
        setEditingId(null);
        setProductForm({
            title: '', category: categories.length > 0 ? categories[0].slug : '', renderer: 'V-Ray', 
            image: '', file_size: '', price: 0, description: '', polys: '', verts: '', formats: '', textures: '', uv_mapping: 'Yes', download_link: ''
        });
    };

    const handleProductSubmit = async (e) => {
        e.preventDefault();
        if(!productForm.title || !productForm.image) { alert('عنوان و تصویر الزامی است'); return; }

        setIsSubmittingProduct(true);
        try {
            if (editingId) {
                const res = await axios.put(`${API_BASE}/products.php`, { ...productForm, id: editingId });
                if(res.data.success) {
                    alert('محصول با موفقیت ویرایش شد!');
                    setProducts(prev => prev.map(p => p.id === editingId ? { ...productForm, id: editingId, is_free: productForm.price == 0 } : p));
                    cancelEditing();
                } else { alert('خطا: ' + res.data.error); }
            } else {
                const res = await axios.post(`${API_BASE}/products.php`, productForm);
                if(res.data.success) {
                    alert('محصول با موفقیت اضافه شد!');
                    const newProdObj = { ...productForm, id: res.data.id, is_free: productForm.price == 0 };
                    setProducts([newProdObj, ...products]);
                    cancelEditing();
                } else { alert('خطا: ' + res.data.error); }
            }
        } catch (error) { alert('خطا در ارتباط با سرور.'); } finally { setIsSubmittingProduct(false); }
    };

    const handleDeleteProduct = async (id) => {
        if(!window.confirm('آیا از حذف این محصول مطمئن هستید؟')) return;
        try {
            await axios.delete(`${API_BASE}/products.php`, { data: { id } });
            setProducts(prev => prev.filter(p => p.id !== id));
            if(editingId === id) cancelEditing();
        } catch (error) { alert('خطا در حذف محصول'); }
    };

    // --- مدیریت دسته‌بندی‌ها ---
    const handleCategoryChange = (e) => {
        setCategoryForm({ ...categoryForm, [e.target.name]: e.target.value });
    };

    const startEditingCategory = (cat) => {
        setEditingCategoryId(cat.id);
        setCategoryForm({ slug: cat.slug, label: cat.label, icon: cat.icon });
    };

    const cancelEditingCategory = () => {
        setEditingCategoryId(null);
        setCategoryForm({ slug: '', label: '', icon: 'Box' });
    };

    const handleCategorySubmit = async (e) => {
        e.preventDefault();
        if(!categoryForm.slug || !categoryForm.label) { alert('شناسه انگلیسی و نام فارسی الزامی است'); return; }

        setIsSubmittingCategory(true);
        try {
            if (editingCategoryId) {
                const res = await axios.put(`${API_BASE}/categories.php`, { ...categoryForm, id: editingCategoryId });
                if(res.data.success) {
                    setCategories(prev => prev.map(c => c.id === editingCategoryId ? { ...categoryForm, id: editingCategoryId } : c));
                    cancelEditingCategory();
                } else { alert('خطا: ' + res.data.error); }
            } else {
                const res = await axios.post(`${API_BASE}/categories.php`, categoryForm);
                if(res.data.success) {
                    setCategories([...categories, { ...categoryForm, id: res.data.id }]);
                    cancelEditingCategory();
                } else { alert('خطا: ' + res.data.error); }
            }
        } catch (error) { alert('خطا در ارتباط با سرور.'); } finally { setIsSubmittingCategory(false); }
    };

    const handleDeleteCategory = async (id) => {
        if(!window.confirm('حذف این دسته‌بندی؟')) return;
        try {
            await axios.delete(`${API_BASE}/categories.php`, { data: { id } });
            setCategories(prev => prev.filter(c => c.id !== id));
            if(editingCategoryId === id) cancelEditingCategory();
        } catch (error) { alert('خطا در حذف'); }
    };

    // --- توابع محتوا و اخبار ---
    const handleApproveComment = async (id) => {
        try {
            await axios.put(`${API_BASE}/comments.php`, { id, approved: 1 });
            setComments(prev => prev.map(c => c.id === id ? { ...c, is_approved: true } : c));
        } catch (error) { alert('خطا در تایید'); }
    };
    const handleDeleteComment = async (id) => {
        if(!window.confirm('حذف نظر؟')) return;
        try {
            await axios.delete(`${API_BASE}/comments.php`, { data: { id } });
            setComments(prev => prev.filter(c => c.id !== id));
        } catch (error) { alert('خطا در حذف'); }
    };
    const handleAddNews = async (e) => {
        e.preventDefault();
        setIsSubmittingNews(true);
        try {
            const res = await axios.post(`${API_BASE}/news.php`, { title: newNewsTitle, text: newNewsText, author: 'مدیریت' });
            if(res.data.success) {
                setNews([{ id: Date.now(), title: newNewsTitle, text: newNewsText, created_at: new Date() }, ...news]);
                setNewNewsTitle(''); setNewNewsText('');
            }
        } catch (error) { alert('خطا'); } finally { setIsSubmittingNews(false); }
    };
    const handleDeleteNews = async (id) => {
        if(!window.confirm('حذف خبر؟')) return;
        try {
            await axios.delete(`${API_BASE}/news.php`, { data: { id } });
            setNews(prev => prev.filter(n => n.id !== id));
        } catch (error) { alert('خطا'); }
    };

    const pendingList = comments.filter(c => !c.is_approved);
    const approvedList = comments.filter(c => c.is_approved);

    const getCategoryLabel = (slug) => {
        const cat = categories.find(c => c.slug === slug);
        return cat ? cat.label : slug;
    };

    if (!isAdmin) {
        return (
            <div className="min-h-screen bg-[#081b29] flex flex-col">
                <style>{`
                    .login-box-wrapper { position: relative; width: 100%; max-width: 800px; height: 65vh; min-height: 500px; background: #081b29; box-shadow: 0 0 50px 0 #00a6bc; border: 3px solid #00ffff; overflow: hidden; border-radius: 8px; }
                    .neon-input { border: none; outline: none; background: none; border-bottom: 2px solid #eee; width: 100%; padding: 10px 10px 10px 0; font-size: 17px; color: #0ef; transition: border-color 0.3s; }
                    .neon-input::placeholder { color: #eee; opacity: 0.7; }
                    .neon-input:focus { border-bottom-color: #0ef; }
                    .neon-submit { border: none; outline: none; width: 100%; margin-top: 25px; padding: 12px 0; font-size: 20px; border-radius: 40px; letter-spacing: 1px; cursor: pointer; background: linear-gradient(45deg, #0ef, #c800ff); color: #fff; font-weight: bold; transition: transform 0.2s; }
                    .neon-submit:hover { transform: scale(1.02); box-shadow: 0 0 15px #0ef; }
                `}</style>
                <div className="bg-slate-900 border-b border-slate-800 p-4 flex justify-between items-center shadow-lg relative z-20">
                    <div className="flex items-center gap-3">
                         <div className="bg-yellow-500/20 p-2 rounded-lg text-yellow-500"><Settings size={24} /></div>
                         <h1 className="text-xl font-bold text-white">پنل مدیریت free3dmax.ir</h1>
                    </div>
                    <button onClick={onBack} className="text-slate-400 hover:text-white flex items-center gap-2 text-sm">
                        بازگشت به سایت<LogOut size={16} />
                    </button>
                </div>
                <div className="flex-grow flex items-center justify-center p-4">
                    <div className="login-box-wrapper grid grid-cols-1 md:grid-cols-2 animate-fade-in-up">
                         <div className="flex flex-col justify-center items-center p-8 relative z-10">
                            <h2 className="text-3xl font-bold text-white mb-2">خوش آمدید</h2>
                            <p className="text-[#0ef] mb-10 text-sm">ورود به پنل مدیریت</p>
                            <form onSubmit={handleLogin} className="w-full max-w-[260px] flex flex-col items-center">
                                <div className="w-full mb-6">
                                    <input type="text" className="neon-input" placeholder="نام کاربری" value={username} onChange={(e) => setUsername(e.target.value)} />
                                </div>
                                <div className="w-full mb-4 relative">
                                    <input type={showPassword ? "text" : "password"} className="neon-input" placeholder="رمز عبور" value={password} onChange={(e) => setPassword(e.target.value)} />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute left-0 top-3 text-[#0ef] hover:text-white transition-colors">
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                                {loginError && <p className="text-red-400 text-xs w-full text-right mt-2">{loginError}</p>}
                                <button type="submit" className="neon-submit">ورود</button>
                            </form>
                         </div>
                         <div className="hidden md:flex flex-col justify-center items-end text-right p-10 relative bg-gradient-to-r from-[#0ef] to-[#c800ff]" style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 20% 100%)" }}>
                            <div className="ml-auto w-full max-w-[280px] mt-[-50px]">
                                <h2 className="text-4xl font-bold text-white mb-4 drop-shadow-md">free3dmax</h2>
                                <p className="text-lg text-white/90 leading-relaxed font-medium drop-shadow-sm">این مجموعه توسط گروه تخصصی ارکانی عمران پیاده سازی شده است.</p>
                            </div>
                         </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 pt-28 pb-12 px-4 animate-fade-in-up">
            <div className="container mx-auto">
                <div className="flex flex-col lg:flex-row justify-between items-center mb-8 gap-4 bg-white p-4 rounded-3xl shadow-sm border border-slate-100 overflow-x-auto">
                    <h2 className="text-xl font-black text-slate-800 px-2 shrink-0">پیشخوان مدیریت</h2>
                    <div className="flex bg-slate-100 rounded-2xl p-1.5 shrink-0">
                        <button onClick={() => setMainTab('products')} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${mainTab === 'products' ? 'bg-white shadow text-purple-600' : 'text-slate-500 hover:text-slate-700'}`}>
                            <Box size={18}/> محصولات
                        </button>
                        <button onClick={() => setMainTab('categories')} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${mainTab === 'categories' ? 'bg-white shadow text-amber-600' : 'text-slate-500 hover:text-slate-700'}`}>
                            <FolderTree size={18}/> دسته‌بندی‌ها
                        </button>
                        <button onClick={() => setMainTab('content')} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${mainTab === 'content' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>
                            <LayoutGrid size={18}/> محتوا و اخبار
                        </button>
                    </div>
                    <button onClick={() => setIsAdmin(false)} className="text-red-500 flex items-center gap-2 font-bold text-sm bg-red-50 px-4 py-2 rounded-xl hover:bg-red-100 transition-colors shrink-0">
                        <LogOut size={18}/> خروج
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={40}/></div>
                ) : (
                    <>
                        {/* --- تب دسته‌بندی‌ها --- */}
                        {mainTab === 'categories' && (
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                                <div className="lg:col-span-4 h-fit sticky top-32">
                                    <div className={`bg-white p-6 rounded-3xl shadow-sm border transition-all ${editingCategoryId ? 'border-yellow-400 shadow-yellow-100' : 'border-slate-100'}`}>
                                        <div className="flex justify-between items-center mb-6 border-b border-slate-50 pb-4">
                                            <h3 className="font-bold flex gap-2 text-slate-800">
                                                {editingCategoryId ? <><Edit className="text-yellow-500"/> ویرایش دسته‌بندی</> : <><Plus className="text-amber-500"/> دسته‌بندی جدید</>}
                                            </h3>
                                            {editingCategoryId && (
                                                <button onClick={cancelEditingCategory} className="text-xs flex items-center gap-1 text-red-500 bg-red-50 px-3 py-1 rounded-lg hover:bg-red-100">
                                                    <X size={14}/> انصراف
                                                </button>
                                            )}
                                        </div>
                                        <form onSubmit={handleCategorySubmit} className="space-y-4">
                                            <div>
                                                <label className="text-xs font-bold text-slate-500 mb-1 block">شناسه انگلیسی (برای دیتابیس)</label>
                                                <input required name="slug" placeholder="مثال: sofa" value={categoryForm.slug} onChange={handleCategoryChange} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm dir-ltr outline-none focus:border-amber-500" disabled={!!editingCategoryId}/>
                                                <p className="text-[10px] text-slate-400 mt-1">از حروف انگلیسی و خط تیره استفاده کنید. (غیرقابل ویرایش پس از ثبت)</p>
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-slate-500 mb-1 block">نام فارسی (نمایش در سایت)</label>
                                                <input required name="label" placeholder="مثال: مبلمان راحتی" value={categoryForm.label} onChange={handleCategoryChange} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm outline-none focus:border-amber-500" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-slate-500 mb-1 block">نام آیکون (Lucide)</label>
                                                <input required name="icon" placeholder="مثال: Box" value={categoryForm.icon} onChange={handleCategoryChange} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm dir-ltr outline-none focus:border-amber-500" />
                                            </div>
                                            <button disabled={isSubmittingCategory} className={`w-full text-white py-3.5 rounded-xl font-bold transition-all shadow-lg ${editingCategoryId ? 'bg-yellow-500 hover:bg-yellow-600 shadow-yellow-500/20' : 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/20'}`}>
                                                {isSubmittingCategory ? <Loader2 className="animate-spin mx-auto"/> : (editingCategoryId ? 'ذخیره تغییرات' : 'ثبت دسته‌بندی')}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                                <div className="lg:col-span-8">
                                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
                                        <h3 className="font-bold mb-6 flex gap-2 text-slate-800 border-b border-slate-50 pb-4">
                                            <FolderTree className="text-amber-500"/> لیست دسته‌بندی‌ها ({categories.length})
                                        </h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto custom-scrollbar p-1">
                                            {categories.map(cat => (
                                                <div key={cat.id} className="flex justify-between items-center bg-slate-50 p-4 rounded-2xl border border-slate-100 hover:border-amber-200 transition-colors">
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-slate-800 text-sm">{cat.label}</span>
                                                        <span className="text-[10px] text-slate-400 dir-ltr text-left mt-1 flex items-center gap-1"><Tag size={10}/> {cat.slug} | Icon: {cat.icon}</span>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <button onClick={() => startEditingCategory(cat)} className="text-blue-500 bg-white p-2 rounded-xl border border-blue-100 hover:bg-blue-500 hover:text-white transition-all shadow-sm"><Edit size={16}/></button>
                                                        <button onClick={() => handleDeleteCategory(cat.id)} className="text-red-400 bg-white p-2 rounded-xl border border-red-100 hover:bg-red-500 hover:text-white transition-all shadow-sm"><Trash2 size={16}/></button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* --- تب محصولات --- */}
                        {mainTab === 'products' && (
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                                <div className="lg:col-span-5 h-fit">
                                    <div className={`bg-white p-6 rounded-3xl shadow-sm border transition-all ${editingId ? 'border-yellow-400 shadow-yellow-100' : 'border-slate-100'} sticky top-32`}>
                                        <div className="flex justify-between items-center mb-6 border-b border-slate-50 pb-4">
                                            <h3 className="font-bold flex gap-2 text-slate-800">
                                                {editingId ? <><Edit className="text-yellow-500"/> ویرایش محصول</> : <><Plus className="text-purple-500"/> افزودن محصول جدید</>}
                                            </h3>
                                            {editingId && (
                                                <button onClick={cancelEditing} className="text-xs flex items-center gap-1 text-red-500 bg-red-50 px-3 py-1 rounded-lg hover:bg-red-100">
                                                    <X size={14}/> انصراف
                                                </button>
                                            )}
                                        </div>
                                        <form onSubmit={handleProductSubmit} className="space-y-4">
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="col-span-2">
                                                    <label className="text-xs font-bold text-slate-500 mb-1 block">عنوان محصول</label>
                                                    <input required name="title" value={productForm.title} onChange={handleInputChange} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm focus:bg-white focus:border-purple-500 transition-all outline-none" />
                                                </div>
                                                <div>
													<label className="text-xs font-bold text-slate-500 mb-1 block">دسته‌بندی</label>
													<select name="category" value={productForm.category} onChange={handleInputChange} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm outline-none cursor-pointer">
														{categories.map(cat => (
                                                            <option key={cat.id} value={cat.slug}>{cat.label}</option>
                                                        ))}
													</select>
												</div>
                                                <div>
                                                    <label className="text-xs font-bold text-slate-500 mb-1 block">قیمت (تومان)</label>
                                                    <input type="number" name="price" placeholder="0 = رایگان" value={productForm.price} onChange={handleInputChange} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm dir-ltr outline-none" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-slate-500 mb-1 block">توضیحات محصول</label>
                                                <textarea name="description" rows="3" value={productForm.description} onChange={handleInputChange} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm focus:bg-white transition-all outline-none resize-none" />
                                            </div>
                                            <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100">
                                                <p className="text-xs font-black text-slate-400 mb-2 flex items-center gap-1"><Layers size={12}/> مشخصات فنی</p>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div><label className="text-[10px] font-bold text-slate-500 block">Render Engine</label><input name="renderer" value={productForm.renderer} onChange={handleInputChange} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs dir-ltr outline-none" /></div>
                                                    <div><label className="text-[10px] font-bold text-slate-500 block">File Size</label><input name="file_size" value={productForm.file_size} onChange={handleInputChange} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs dir-ltr outline-none" /></div>
                                                    <div><label className="text-[10px] font-bold text-slate-500 block">Polygons</label><input name="polys" value={productForm.polys} onChange={handleInputChange} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs dir-ltr outline-none" /></div>
                                                    <div><label className="text-[10px] font-bold text-slate-500 block">Vertices</label><input name="verts" value={productForm.verts} onChange={handleInputChange} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs dir-ltr outline-none" /></div>
                                                    <div><label className="text-[10px] font-bold text-slate-500 block">Formats</label><input name="formats" value={productForm.formats} onChange={handleInputChange} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs dir-ltr outline-none" /></div>
                                                    <div><label className="text-[10px] font-bold text-slate-500 block">Textures</label><input name="textures" value={productForm.textures} onChange={handleInputChange} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs dir-ltr outline-none" /></div>
                                                    <div>
                                                        <label className="text-[10px] font-bold text-slate-500 block">UV Mapping</label>
                                                        <select name="uv_mapping" value={productForm.uv_mapping} onChange={handleInputChange} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs dir-ltr outline-none">
                                                            <option value="Yes">Yes</option>
                                                            <option value="No">No</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-3 pt-2">
                                                <div>
                                                    <label className="text-xs font-bold text-slate-500 mb-1 block">لینک تصویر (URL)</label>
                                                    <div className="relative">
                                                        <input required name="image" placeholder="https://..." value={productForm.image} onChange={handleInputChange} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 pl-10 text-sm dir-ltr outline-none" />
                                                        <ImageIcon className="absolute left-3 top-3 text-slate-400" size={18}/>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-slate-500 mb-1 block">لینک دانلود فایل</label>
                                                    <div className="relative">
                                                        <input name="download_link" placeholder="https://..." value={productForm.download_link} onChange={handleInputChange} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 pl-10 text-sm dir-ltr outline-none" />
                                                        <LinkIcon className="absolute left-3 top-3 text-slate-400" size={18}/>
                                                    </div>
                                                </div>
                                            </div>
                                            <button disabled={isSubmittingProduct} className={`w-full text-white py-3.5 rounded-xl font-bold transition-all shadow-lg ${editingId ? 'bg-yellow-500 hover:bg-yellow-600 shadow-yellow-500/20' : 'bg-slate-900 hover:bg-slate-800 shadow-slate-900/20'}`}>
                                                {isSubmittingProduct ? <Loader2 className="animate-spin mx-auto"/> : (editingId ? 'ذخیره تغییرات' : 'ثبت محصول')}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                                <div className="lg:col-span-7">
                                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
                                        <h3 className="font-bold mb-6 flex gap-2 text-slate-800 border-b border-slate-50 pb-4">
                                            <Box className="text-purple-500"/> آرشیو محصولات ({products.length})
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[800px] overflow-y-auto custom-scrollbar p-1">
                                            {products.map(prod => (
                                                <div key={prod.id} className={`flex gap-4 bg-slate-50 p-3 rounded-2xl border transition-colors group ${editingId === prod.id ? 'border-yellow-400 bg-yellow-50' : 'border-slate-100 hover:border-purple-200'}`}>
                                                    <img src={prod.image} className="w-24 h-24 object-cover rounded-xl bg-white shadow-sm" alt="img" onError={(e)=>{e.target.src='https://via.placeholder.com/150'}} />
                                                    <div className="flex-1 flex flex-col justify-between py-1">
                                                        <div>
                                                            <h4 className="font-bold text-slate-800 text-sm mb-1 line-clamp-1">{prod.title}</h4>
                                                            <div className="flex gap-2 flex-wrap">
                                                                <span className="text-[10px] text-slate-500 bg-white px-2 py-0.5 rounded border">
                                                                    {getCategoryLabel(prod.category)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="flex justify-between items-end">
                                                            <span className={`text-xs font-bold ${Number(prod.price) === 0 ? 'text-green-600' : 'text-slate-800'}`}>
                                                                {Number(prod.price) === 0 ? 'رایگان' : `${new Intl.NumberFormat('fa-IR').format(prod.price)} T`}
                                                            </span>
                                                            <div className="flex gap-1">
                                                                <button onClick={() => startEditing(prod)} className="text-blue-500 bg-white p-2 rounded-xl border border-blue-100 hover:bg-blue-500 hover:text-white transition-all shadow-sm"><Edit size={16}/></button>
                                                                <button onClick={() => handleDeleteProduct(prod.id)} className="text-red-400 bg-white p-2 rounded-xl border border-red-100 hover:bg-red-500 hover:text-white transition-all shadow-sm"><Trash2 size={16}/></button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* --- تب محتوا و اخبار --- */}
                        {mainTab === 'content' && (
                             <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 h-[600px] flex flex-col">
                                    <div className="flex justify-between items-center mb-6 border-b border-slate-50 pb-4">
                                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                            <MessageSquare className="text-blue-500"/> مدیریت نظرات
                                        </h3>
                                        <div className="flex gap-1 bg-slate-50 p-1 rounded-xl">
                                            <button onClick={()=>setActiveCommentTab('pending')} className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${activeCommentTab==='pending'?'bg-white shadow text-slate-800':'text-slate-400'}`}>جدید ({pendingList.length})</button>
                                            <button onClick={()=>setActiveCommentTab('approved')} className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${activeCommentTab==='approved'?'bg-white shadow text-green-600':'text-slate-400'}`}>تایید شده</button>
                                        </div>
                                    </div>
                                    <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3">
                                        {(activeCommentTab==='pending'?pendingList:approvedList).length === 0 ? (
                                            <p className="text-center text-slate-400 py-10 text-sm">لیست خالی است.</p>
                                        ) : (
                                            (activeCommentTab==='pending'?pendingList:approvedList).map(c => (
                                                <div key={c.id} className={`p-4 rounded-2xl border transition-all ${c.is_approved ? 'bg-slate-50 border-slate-100' : 'bg-orange-50/50 border-orange-100'}`}>
                                                    <div className="flex justify-between mb-2">
                                                        <span className="font-bold text-sm text-slate-800">{c.name}</span>
                                                        <span className="text-[10px] text-slate-400">{new Date(c.created_at).toLocaleDateString('fa-IR')}</span>
                                                    </div>
                                                    <p className="text-xs text-slate-600 leading-5 mb-3">{c.text}</p>
                                                    <div className="flex justify-end gap-2">
                                                        {!c.is_approved && (
                                                            <button onClick={()=>handleApproveComment(c.id)} className="text-xs bg-green-50 text-green-600 px-3 py-1.5 rounded-lg hover:bg-green-100 font-bold flex gap-1 items-center"><CheckCircle size={14}/> تایید</button>
                                                        )}
                                                        <button onClick={()=>handleDeleteComment(c.id)} className="text-xs bg-red-50 text-red-500 px-3 py-1.5 rounded-lg hover:bg-red-100 font-bold flex gap-1 items-center"><Trash2 size={14}/> حذف</button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-6">
                                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
                                        <h3 className="font-bold mb-4 flex gap-2 text-slate-800"><Plus className="text-blue-500"/> انتشار خبر جدید</h3>
                                        <div className="space-y-3">
                                            <input value={newNewsTitle} onChange={e=>setNewNewsTitle(e.target.value)} placeholder="عنوان خبر" className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm outline-none focus:bg-white transition-colors"/>
                                            <textarea value={newNewsText} onChange={e=>setNewNewsText(e.target.value)} placeholder="متن خبر..." rows="3" className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm outline-none focus:bg-white transition-colors resize-none"/>
                                            <button onClick={handleAddNews} disabled={isSubmittingNews} className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold shadow-lg shadow-slate-900/10">
                                                {isSubmittingNews ? '...' : 'انتشار خبر'}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 max-h-[300px] overflow-y-auto custom-scrollbar">
                                        <h3 className="font-bold mb-4 flex gap-2 text-slate-800"><FileText className="text-blue-500"/> آرشیو اخبار</h3>
                                        {news.map(n => (
                                            <div key={n.id} className="flex justify-between items-start border-b border-slate-50 py-3 last:border-0 hover:bg-slate-50 p-2 rounded-lg transition-colors group">
                                                <div>
                                                    <span className="text-sm font-bold text-slate-800 block">{n.title}</span>
                                                    <span className="text-[10px] text-slate-400">{new Date(n.created_at).toLocaleDateString('fa-IR')}</span>
                                                </div>
                                                <button onClick={()=>handleDeleteNews(n.id)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={16}/></button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminPanel;